import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useFilters, useSortBy, useTable } from 'react-table';
import type { FixedSizeList } from 'react-window';

import type { TableColumn, TableColumnInstance, Tournament } from '@swf/poker';
import {
  CELL_BUY_IN,
  CELL_GAME,
  CELL_NAME,
  CELL_PLAYERS,
  CELL_PRIZE_POOL,
  CELL_START_STATE,
  DEFAULT_COLUMN,
  TableBody,
  TableHead,
  useTableSearchFilter,
  useTableSelectFirstRow
} from '@swf/poker';

import { TABLE_DEFAULT_HEIGHT, TABLE_ROW_SIZE } from '../../constants';
import useTableRowsCount from '../../hooks/useTableRowsCount';
import { usePokerMultiTableTournamentsWidget } from '../../model';

import styles from './TournamentsTable.scss';
import useVirtualStickyListScroll from './hooks/useVirtualStickyListScroll';

export const COLUMNS: TableColumn[] = [
  CELL_START_STATE,
  CELL_NAME,
  CELL_GAME,
  CELL_BUY_IN,
  CELL_PRIZE_POOL,
  CELL_PLAYERS
];

const FixedSizeListWrapper = forwardRef<HTMLDivElement>((props, ref) => (
  <div ref={ref} {...props} style={{ overflow: 'hidden important!' }} />
));

const TournamentsTable: React.FC = () => {
  const [fixedListHeight, setFixedListHeight] = useState(TABLE_DEFAULT_HEIGHT);
  const { widgetSelect, widgetDispatch } = usePokerMultiTableTournamentsWidget();
  const loading = useSelector(widgetSelect.isLoading);
  const filters = useSelector(widgetSelect.getTableFilters);
  const sortRule = useSelector(widgetSelect.getTableSortRule);
  const selectedRowId = useSelector(widgetSelect.getSelectedTournamentId);
  const tournamentList = useSelector(widgetSelect.getTournamentList);
  const tableBodyRef = useRef<HTMLDivElement>(null);
  const fixedListRef = useRef<FixedSizeList>(null);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, setFilter } =
    useTable<Tournament>(
      {
        columns: COLUMNS,
        data: tournamentList,
        initialState: { sortBy: [sortRule], filters },
        defaultColumn: DEFAULT_COLUMN
      },
      useFilters,
      useSortBy
    );

  const handleColumnClick = (column: TableColumnInstance) =>
    widgetDispatch.setTableSortRule({ id: column.id, desc: !column.isSortedDesc });

  const handleRowClick = (tournamentId: number) =>
    widgetDispatch.setSelectedTournamentId(tournamentId);

  useTableSearchFilter(filters, setFilter);
  useTableRowsCount(rows.length);
  useTableSelectFirstRow({
    firstRowId: rows[0]?.original.id,
    selectedRowId,
    setSelectedTournamentId: handleRowClick
  });
  useVirtualStickyListScroll(tableBodyRef, fixedListRef);

  useEffect(() => {
    setFixedListHeight(window.innerHeight);
  }, []);

  return (
    <div className={styles.container} data-testid="tournaments-table">
      <div className={styles.table} {...getTableProps()}>
        <TableHead headerGroups={headerGroups} handleColumnClick={handleColumnClick} />
        <TableBody
          ref={tableBodyRef}
          className={styles.tableBody}
          height={fixedListHeight}
          width="100%"
          outerElementType={FixedSizeListWrapper}
          fixedListRef={fixedListRef}
          bodyProps={getTableBodyProps()}
          itemData={{ rows, prepareRow, selectedRowId, onRowClick: handleRowClick }}
          itemSize={TABLE_ROW_SIZE}
          itemCount={rows.length}
          loading={loading}
        />
      </div>
    </div>
  );
};

export default TournamentsTable;
